import clsx from 'clsx';
import type {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';
import InputT from './InputT';

type InputProps<T extends FieldValues> = {
  id: string;
  label: string;
  control?: Control<T>;
  field?: Path<T>;
  labelClassName?: string;
  options?: RegisterOptions<T>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  imgSrc?: string;
} & React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;

export function CheckboxInputT<T extends FieldValues>({
  id,
  label,
  control,
  field,
  labelClassName = '',
  onChange,
  imgSrc,
  ...restParams
}: InputProps<T>) {
  const { className, checked, disabled } = restParams || {};

  let defaultClasses = [
    'flex',
    'items-center',
    'justify-start',
    'font-light',
    'text-xs',
    'leading-5',
    'my-3',
    disabled ? 'cursor-default' : 'cursor-pointer',
    'select-none',
    'appearance-none',
    'relative',
    disabled ? 'opacity-40' : '',
    disabled ? 'pointer-events-none' : '',
    'w-fit',
  ];
  // If className contains a my-* ( margin ) class, remove my-3 from defaultClasses, otherwise the my-3 is always overwritten the my-* class that is coming from the parent component
  if (className?.includes('my-')) {
    defaultClasses = defaultClasses.filter((style) => !style.startsWith('my-'));
  }

  return (
    <label htmlFor={id} className={clsx([...defaultClasses, className])}>
      <InputT
        id={id}
        type='checkbox'
        field={field}
        control={control}
        options={{
          onChange,
          disabled,
        }}
        checked={checked}
        className='cursor-pointer'
      />
      <div className='flex items-center pl-2'>
        {imgSrc && (
          <img src={imgSrc} alt={label} className='inline-flex w-4 h-4 mr-1' />
        )}
        <span className={`${labelClassName}`}>{label}</span>
      </div>
    </label>
  );
}

export default CheckboxInputT;
